import { GetProvidersParams, GetRsaSign, UploadConfigParams, UploadConfigResponse } from './model.d'
import request from '@/utils/request'
import type {
  DrugNameCodePack,
  ProviderListItem,
  ProviderListItemForApply,
  ProviderSupplierName,
  SearchDrugInfoParams,
  getImgUrlParams,
  QueryProviderSupplierNameParams,
  WarehouseListItem,
  ApplyGoodInfoRes,
  AreaItem,
  AsyncDownloadListItem
} from './type.d'
import {
  CommonNameParams,
  FactoryNameParams,
  CommonNameResponse,
  FactoryNameResponse,
  GoodsTypeResponse,
  RecipeTypeResponse,
  GoodsTypeParams,
  LeyoProviderItem,
  SupplyOptionItem
} from './model'
import type { PagerCommon, PaginationCommon } from '@/api/global'
import type { JztChannelItem } from '@/utils/jztChannelListConfig'

/**
 * 获取RSA签名
 * @param data 请求参数
 * @author 李发军
 */
export function getRsaSign(data: GetRsaSign) {
  // tag(平台标识)：1-中台，2-结算系统，3-erp
  const tag = 1
  return request.post<string>({
    url: `/commonSearch/rsaSign/${tag}`,
    data
  })
}

/**
 * @description 合作子公司下拉列表查询
 * @author 李伟华
 */
export function queryProviderOption(data: GetProvidersParams = {}) {
  return request.post<ProviderListItem[]>({
    url: '/commonSearch/queryProviderOption',
    data
  })
}

/**
 * @description 申报 - 合作子公司下拉列表查询
 * @author 周逸轩
 */
export function queryProvidersForApply(data: GetProvidersParams = {}) {
  return request.post<ProviderListItemForApply[]>({
    url: '/commonSearch/queryProvidersForApply',
    data
  })
}

/**
 * 申报-合作仓下拉列表查询
 * @backend 周逸轩
 */
export function queryWarehousesForApply(data: CommonSearch.QueryWarehousesForApply) {
  return request.post<CommonSearch.QueryWarehousesForApplyRecord[]>({
    url: '/commonSearch/queryWarehousesForApply',
    data
  })
}

/**
 * @description 合作子公司下拉列表查询(只查已申报公司)
 * @author 吴晓军
 */
export function queryProviderRelated(data: GetProvidersParams = {}) {
  return request.post<ProviderListItem[]>({
    url: '/commonSearch/queryProviderRelated',
    data
  })
}

/**
 * @description 合作子公司下拉列表查询（简称规范）
 * 【1176683】子公司简称规范需求新增使用
 * @author 周杰雄
 */
export function queryProviderPurchasedSim(data: GetProvidersParams = {}) {
  return request.post<ProviderListItem[]>({
    url: '/purchaseRecordNotScw/queryProviderPurchasedSim',
    data
  })
}

/**
 * @description 查询条件中的根据商品编码或商品名获取商品信息
 * @author 李伟华
 */
export function searchDrugInfoByCodeOrName(data: SearchDrugInfoParams) {
  return request.post<DrugNameCodePack[]>({
    url: '/commonSearch/searchDrugInfoByCodeOrName',
    data
  })
}

/**
 * @description 获取模板下载路径
 * @author 周杰雄
 */
export function getImgUrl(data: getImgUrlParams) {
  return request.post<string>({
    url: '/commonSearch/getImgUrl',
    params: data
  })
}

// 【李伟华】- 通用名模糊查询
export function queryCommonName(data: CommonNameParams) {
  return request.post<CommonNameResponse>({
    url: '/commonSearch/queryCommonName',
    params: data
  })
}

// 【李伟华】- 厂家模糊查询
export function queryFactoryName(data: FactoryNameParams) {
  return request.post<FactoryNameResponse>({
    url: '/commonSearch/queryFactoryName',
    params: data
  })
}

// 【李伟华】- 商品分类下拉列表查询
export function queryGoodsTypeOption(data: GoodsTypeParams) {
  return request.post<GoodsTypeResponse>({
    url: '/commonSearch/queryGoodsTypeOption',
    params: data
  })
}

// 【李伟华】- 处方分类下拉列表查询
export function queryRecipeTypeOption() {
  return request.post<RecipeTypeResponse>({
    url: '/commonSearch/queryRecipeTypeOption'
  })
}

// 【李伟华】- 获取七牛上传图片token,请求参数为调用平台名称(scw、pms等)
export function getQiniuUpLoadToken(data: UploadConfigParams) {
  return request.post<UploadConfigResponse>({
    url: '/commonSearch/getQiniuUpLoadToken',
    params: data
  })
}

// 【周逸轩】- 检查是否需要展示操作指引
export function getOperationHint() {
  return request.post<boolean>({
    url: '/user/operation-hint'
  })
}

//谢运鹏】-供应商模糊查询
export function queryProviderSupplierName(data: QueryProviderSupplierNameParams) {
  return request.post<{
    list: ProviderSupplierName[]
  }>({
    url: '/commonSearch/queryProviderSupplierName',
    data
  })
}

/**
 * 【周逸轩】根据商品编码获取最后一次申报及商品信息
 */
export function queryBrands(params: string): Promise<string[]> {
  return request.get(
    {
      url: `/commonSearch/queryBrands?brand=${params}`
    },
    {
      isSendAuth8Params: true
    }
  )
}

/**
 * 【周逸轩】根据商品编码获取最后一次申报及商品信息
 */
export function getLastApplyGoodInfo(params: {
  provDrugCode: string
  providerId: number
}): Promise<ApplyGoodInfoRes> {
  return request.get({
    url: '/commonSearch/getLastApplyGoodInfo',
    params
  })
}

/**
 * @description 仓库下拉列表查询
 * @param providerId 公司ID，-1表示全部
 * @author 甘楚雄
 */
export function getWarehouseList(params: { providerId: number }) {
  return request.post<WarehouseListItem[]>({
    url: '/commonSearch/warehouseList',
    params
  })
}

/**
 * 【周逸轩】获取省份列表
 */
export function getProvinces(): Promise<AreaItem[]> {
  return request.get({
    url: '/commonSearch/getProvinces'
  })
}

/**
 * 【陈健容】查询乐药子公司列表
 */
export function leyoProviderList(): Promise<LeyoProviderItem[]> {
  return request.post(
    {
      url: '/web/providerAuth/leyoProviderList/srv300'
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【陈健容】查询中台库的渠道信息
 */
export function datacenterSupplierOptions(data: {
  current: ''
  size: ''
  supplierName: ''
}): Promise<
  {
    supplierCode: number
    supplierName: string
  }[]
> {
  return request.post(
    {
      url: '/web/business/datacenterSupplierOptions/srv330',
      data
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【杨道允】账号绑定乐药子公司列表
 */
export function userProviderList(): Promise<LeyoProviderItem[]> {
  return request.post(
    {
      url: '/web/providerAuth/userProviderList/scw3100'
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【石绍明】根据商品名称搜索商品名称
 */
export function searchGoodsName(params: {
  providerId: number
  keyword: string
}): Promise<string[]> {
  return request.post(
    {
      url: '/web/search/searchGoodsName/scw3100',
      params
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【杨道允】异步下载列表
 */
export function supSoaDownloadList(
  data: { taskName: string } & PagerCommon
): Promise<PaginationCommon<AsyncDownloadListItem>> {
  return request.post(
    {
      url: '/web/supplier/soa/supSoaDownloadList/scw3100',
      data
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【石绍明】根据厂家模糊搜索商品厂家
 */
export function searchGoodsFactory(params: {
  providerId: number
  keyword: string
}): Promise<string[]> {
  return request.post(
    {
      url: '/web/search/searchGoodsFactory/scw3100',
      params
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【石绍明】根据国药准字模糊搜索商品国药准字
 */
export function searchGoodsApprovalNo(params: {
  providerId: number
  keyword: string
}): Promise<string[]> {
  return request.post(
    {
      url: '/web/search/searchGoodsApprovalNo/scw3100',
      params
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【李伟华】查询系统对接渠道供应商清单
 */
export function channelSupplierList() {
  return request.get<JztChannelItem[]>(
    {
      url: '/web/business/channelSupplierList/srv29470'
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}

/**
 * 【郑晓东】渠道列表
 */
export function supplyOption(data: {
  providerId: number | string
  size: number
  /** 渠道名称/编码 */
  supplyParam: string
}) {
  return request.post<SupplyOptionItem[]>(
    {
      url: '/web/business/supplyOption/scw3140',
      data
    },
    {
      apiUrl: 'scw-leyo'
    }
  )
}
